import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  Output
} from '@angular/core';
import { Attribute } from 'src/app/shared/models/attributes/attribute';
import { AttributeDefinition } from 'src/app/shared/models/attributes/attributeDefinition';
import { AttributeWrite } from 'src/app/shared/models/attributes/attributeWrite';
import { FlatAttribute } from 'src/app/shared/models/attributes/flatAttribute';
import { lastValueFrom, Observable, Subscription } from 'rxjs';
import { BaseService } from 'src/app/shared/services/base.service';

import _ from 'lodash';
import { AttributeUiType } from 'src/app/shared/models/attributes/attributeUiType';
import { AttributeDefinitionValueListItem } from 'src/app/shared/models/attributes/attributeDefinitionValueListItem';
import {
  formatDays,
  getDurationDisplayFormat
} from 'src/app/shared/models/attributes/durationFormula';
import { AttributesEditorDataSource } from 'src/shared-app-portal/components/attributes-editor/attributesEditorDataSource';

import { DatePipe } from '@angular/common';
import { AttributeEditorMode } from 'src/app/shared/models/attributes/attributeEditorMode';
import { ComplexAttributeWrite } from 'src/app/shared/models/attributes/complexAttributeWrite';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute } from '@angular/router';
import { AuthService } from 'src/app/auth/auth.service';
import { AttributeService } from 'src/app/shared/services/attribute.service';
import { CustomFieldsDescription } from '../../custom-fields-description/custom-fields-description.component';

@Component({
  selector: 'custom-field-inline-editor',
  templateUrl: './custom-field-inline-editor.component.html',
  styleUrls: ['./custom-field-inline-editor.component.scss'],
  standalone: false
})
export class CustomFieldInlineEditorComponent implements OnDestroy {
  private subscriptions: Array<Subscription> = [];
  private _customFieldValues: Attribute[];
  private _customField: AttributeDefinition;
  private firstOpen: boolean = true;
  showPassword: boolean = false;
  visibility_icon: string = 'visibility';
  encryptedFieldValue: string = '***';

  readonly noValueText: string = 'Click to add';

  public isEditing: boolean = false;
  public isSaving: boolean = false;
  public flatAttribute: FlatAttribute;
  public editorDataSource = new AttributesEditorDataSource();
  teamId;
  personId;

  @Input() entityId: string = '';
  @Input() attributeEditorMode: AttributeEditorMode = AttributeEditorMode.PersonDetails;

  @Input()
  get customField(): AttributeDefinition {
    return this._customField;
  }
  set customField(value: AttributeDefinition) {
    this._customField = value;
    this.buildFlatAttributeRepresentation();
  }
  @Input()
  get customFieldValues(): Attribute[] {
    return this._customFieldValues;
  }
  set customFieldValues(value: Attribute[]) {
    this._customFieldValues = value;
    this.buildFlatAttributeRepresentation();
    if (this._customField?.uiType == AttributeUiType.Complex) {
      const cloned = _.cloneDeep(this.customFieldValues);
      this.editorDataSource.load(cloned, [this.customField], this.filterAttribute);
    }
  }
  private _editable: boolean = true;

  @Input()
  set editable(value: boolean) {
    this._editable = value;
  }
  get editable(): boolean {
    // Calculated fields are never directly editable — their value is computed from the formula
    // by the recalc handler. A manual write would be silently overwritten on the next recalc,
    // so we suppress the edit affordance entirely.
    return this._editable && this._customField?.uiType !== AttributeUiType.CalculatedField;
  }
  @Input()
  useDescription: boolean = false;
  @Input()
  saveFn: (attributeDefinitionId: string, attributeWrite: AttributeWrite) => Promise<boolean>;
  @Input()
  deleteFn: (attributeDefinitionId: string) => Observable<any>;
  @Output() addOrUpdateComplexValue: EventEmitter<{
    attributeDefinitionId: string;
    writeModel: ComplexAttributeWrite[];
  }> = new EventEmitter<{ attributeDefinitionId: string; writeModel: ComplexAttributeWrite[] }>();

  constructor(
    private snackBar: BaseService,
    private authService: AuthService,
    private changeDetectorRef: ChangeDetectorRef,
    private datePipe: DatePipe,
    private dialog: MatDialog,
    private route: ActivatedRoute,
    private attributeService: AttributeService
  ) {
    this.route.params.subscribe((params) => {
      this.personId = params['personId'];
    });
    this.route.parent.params.subscribe((params) => {
      if (params['id']) {
        this.teamId = params['id'];
      }
    });
    let current = this.route.snapshot;
    while (current) {
      if (current.params['id']) {
        this.teamId = current.params['id'];
        break;
      }
      if (current.params['teamId']) {
        this.teamId = current.params['teamId'];
        break;
      }
      current = current.parent;
    }
  }

  get firstElementValueListItem(): AttributeDefinitionValueListItem {
    return this._customFieldValues[0]?.attributeDefinitionValueListItem;
  }

  get personDetailsMode(): AttributeEditorMode {
    return AttributeEditorMode.PersonDetails;
  }

  isProfileEditorMode(): boolean {
    return (
      this.attributeEditorMode == AttributeEditorMode.PersonDetails ||
      this.attributeEditorMode == AttributeEditorMode.TeamDetails
    );
  }

  private buildFlatAttributeRepresentation() {
    this.flatAttribute = this.customFieldValues?.length
      ? FlatAttribute.fromAttributes(this.customFieldValues)[0]
      : this.customField
        ? FlatAttribute.fromAttributeDefinition(this.customField)
        : null;
    if (!this.flatAttribute?.attributes) {
      this.encryptedFieldValue = '';
    }
  }

  getInfoTooltipText(): string {
    return this.isStale()
      ? 'This field has not been updated in over a year. It was last updated on ' +
          this.datePipe.transform(this.flatAttribute?.updatedOn, 'mediumDate') +
          ' by ' +
          this.flatAttribute.updatedBy
      : 'Last updated on ' +
          this.datePipe.transform(this.flatAttribute?.updatedOn, 'mediumDate') +
          ' by ' +
          this.flatAttribute.updatedBy;
  }

  showHistoricalText() {
    let fielddataHistoricalModalComponent = this.dialog.open(CustomFieldsDescription, {
      data: {
        attributeName: this.customField.name,
        personId: this.personId,
        teamId: this.teamId,
        entityType: this.customField.entityType,
        description: this.customField.adminDocumentation,
        type: this.customField.uiType
      }
    });
    fielddataHistoricalModalComponent.afterClosed().subscribe((result) => {
      fielddataHistoricalModalComponent = null;
    });
  }

  isStaff() {
    return this.authService.staffLevelAccess;
  }

  public isStale(): boolean {
    return (
      new Date(this.flatAttribute?.updatedOn) <
      new Date(new Date().setFullYear(new Date().getFullYear() - 1))
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }

  openEditor() {
    if (!this.editable) return;

    if (this.firstOpen) {
      this.firstOpen = false;
      const cloned = _.cloneDeep(this.customFieldValues);
      this.editorDataSource.load(cloned, [this.customField], this.filterAttribute);
    }

    this.isEditing = true;
    this.changeDetectorRef.detectChanges();
  }

  formatDisplayValue(attribute: Attribute): string {
    const durationDisplayFormat = getDurationDisplayFormat(this._customField);
    let displayValue = '';

    if (attribute && attribute.type === 'Date' && attribute.hasValue()) {
      displayValue = this.datePipe.transform(attribute.value, 'mediumDate');
    } else if (attribute && attribute.type === 'DateRange') {
      displayValue = attribute.hasValue()
        ? attribute.value.replace('|', '-')
        : this.editable
          ? this.noValueText
          : '';
    } else if (attribute?.hasValue() && durationDisplayFormat) {
      // duration calc field — store is a raw signed-integer day count; render via the formula's
      // displayFormat. Same wiring as profile-custom-field-editor.formatDisplayValue.
      displayValue = formatDays(attribute.value, durationDisplayFormat);
    } else {
      displayValue = attribute.hasValue() ? attribute.value : this.editable ? this.noValueText : '';
    }

    if (displayValue && attribute?.otherValue && attribute?.supportsOtherValue()) {
      displayValue = `${attribute.otherValue} (${displayValue})`;
    }
    return displayValue;
  }

  doSave = async (attributeChanged = true, isDocumentSaving = false) => {
    if (attributeChanged == false) {
      this.cancel();
      return;
    }
    if (!this.formIsValid() || this.isSaving) {
      return;
    }

    this.disableSubmitButton();

    const attr = AttributeWrite.fromAttributes(this.editorDataSource.editable);
    await this.handleAttributeBasedOnValue(attr, isDocumentSaving);
    this.enableSubmitButton();
  };

  handleAttributeBasedOnValue = async (
    attribute: AttributeWrite,
    isDocumentSaving = false
  ): Promise<void> => {
    if (attribute && attribute.hasValue()) {
      const result = await this.saveFn(this.customField.id, attribute);
      if (!result) {
        return;
      }
      if (!isDocumentSaving) {
        this.snackBar.openSnackBar(`${this.customField.name} has been updated.`, true);
        if (this.customField.uiType == AttributeUiType.EncryptedField) {
          this.visibility_icon = 'visibility';
          this.encryptedFieldValue = '***';
          this.showPassword = false;
        }
        this.cancel();
        this.firstOpen = true;
      }
      return;
    }
    await lastValueFrom(this.deleteFn(this.customField.id));
    if (!isDocumentSaving) {
      this.snackBar.openSnackBar(`${this.customField.name} has been deleted.`, true);
      this.cancel();
    }
  };

  preventDefault(event: { preventDefault: () => void }) {
    // prevent the cancel button's mouse-down event from triggering the attribute editor's
    // blur event, which saves the attribute value
    event.preventDefault();
  }

  cancel() {
    this.isEditing = false;
  }

  formIsValid(): boolean {
    return this.editorDataSource.isValid;
  }

  shouldShowHistoricText(): boolean {
    if (!this.flatAttribute) {
      return false;
    }
    if (this.flatAttribute.isDocument) {
      return false;
    }

    if (this.flatAttribute.isEncryptedField) {
      //if there's admin documentation we can show the info button.
      return !!this.flatAttribute.adminDocumentation;
    }

    return this.flatAttribute.hasAttributes || this.flatAttribute.hasComplexAttributeValues;
  }

  togglePasswordVisibility(value: any): void {
    this.showPassword = !this.showPassword;
    if (this.showPassword) {
      this.visibility_icon = 'visibility_off';
      this.attributeService.decryptAttribute(this.customField.id, value).subscribe((result) => {
        if (result) {
          this.encryptedFieldValue = result;
        }
      });
    } else {
      this.visibility_icon = 'visibility';
      this.encryptedFieldValue = '***';
    }
  }

  private disableSubmitButton() {
    this.isSaving = true;
  }

  private enableSubmitButton() {
    this.isSaving = false;
  }

  private filterAttribute = (attributes: Attribute[]) =>
    attributes.filter((a) => a.attributeDefinitionId == this.customField.id);
}
